import React, { useCallback, useState, useEffect } from 'react';
import { FormControl, Button, Form, InputGroup, Badge } from 'react-bootstrap';
import { BiBarcode, BiTrash, BiCheckDouble, BiWrench, BiShieldQuarter } from 'react-icons/bi';
import { FaCheckCircle } from 'react-icons/fa';

// --- SUB-COMPONENT: INDIVIDUAL UNIT ROW ---
const UnitRow = React.memo(({
    unit,
    uIdx,
    itemIndex,
    duplicateSerials,
    handleUnitChange,
    onQtyChange,
    item,
    policies,
    openTagModal,
    generateSerial,
    CONDITION_LIST
}) => {
    const isMissing = !unit.serialNumber?.trim();
    const isDuplicate = duplicateSerials[unit.serialNumber] > 1;
    // 1. LOCAL STATE FOR PERFORMANCE (Isolate keystrokes from parent)
    const [localSerial, setLocalSerial] = React.useState(unit.serialNumber || "");

    // 2. Sync local state when prop changes (e.g., from bulk generation)
    React.useEffect(() => {
        setLocalSerial(unit.serialNumber || "");
    }, [unit.serialNumber]);

    return (
        <div
            className="rounded-3 p-2 d-flex gap-3 align-items-start bg-white border mb-2 shadow-sm"
            style={{ minHeight: '45px' }}
        >
            {/* Index Label */}
            <div className="flex-shrink-0 pt-2" style={{ width: '30px' }}>
                <span className="badge bg-dark rounded-pill x-small">{uIdx + 1}</span>
            </div>

            {/* Serial Input */}
            <div className="flex-grow-1">
                <InputGroup size="sm">
                    <InputGroup.Text className={isDuplicate ? 'bg-warning text-dark' : isMissing ? 'bg-danger text-white' : 'bg-light'}>
                        <BiBarcode />
                    </InputGroup.Text>
                    <div className="position-relative flex-grow-1">
                        <FormControl
                            value={localSerial}
                            placeholder="SERIAL / IMEI"
                            onChange={(e) => setLocalSerial(e.target.value)}
                            onBlur={() => {
                                if (localSerial !== unit.serialNumber) {
                                    handleUnitChange(itemIndex, uIdx, 'serialNumber', localSerial);
                                }
                            }}
                            className={`serial-input font-monospace fw-bold ${isDuplicate ? 'border-warning' : isMissing ? 'border-danger' : ''}`}
                            style={{
                                borderBottom: isDuplicate ? '2px solid #ffc107' : isMissing ? '2px solid #dc3545' : 'none'
                            }}
                        />
                        {isDuplicate && !isMissing && (
                            <Badge bg="warning" text="dark" className="position-absolute shadow-sm" style={{ top: -10, right: 5, fontSize: '0.6rem', fontWeight: 900, border: '1px solid #000', zIndex: 10 }}>DUPLICATE !</Badge>
                        )}
                        {isMissing && (
                            <Badge bg="danger" className="position-absolute shadow-sm" style={{ top: -2, right: 5, fontSize: '0.6rem', fontWeight: 900, zIndex: 10 }}>REQUIRED</Badge>
                        )}
                    </div>
                    <Button
                        variant="light"
                        size="sm"
                        onClick={() => generateSerial(uIdx)}
                        className="px-2 x-small border fw-bold text-dark"
                    >
                        GEN
                    </Button>
                </InputGroup>
            </div>


            {/* Warranty Select */}
            <div style={{ width: '150px' }}>
                <Form.Select
                    size="sm"
                    className="x-small fw-bold"
                    value={unit.warrantyPolicyId || ""}
                    onChange={(e) => handleUnitChange(itemIndex, uIdx, 'warrantyPolicyId', e.target.value)}
                >
                    <option value="">No Warranty</option>
                    {policies.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </Form.Select>
            </div>

            {/* Condition Select */}
            <div style={{ width: '120px' }}>
                <Form.Select
                    size="sm"
                    className="x-small"
                    value={unit.condition || "Brand New"}
                    onChange={(e) => handleUnitChange(itemIndex, uIdx, 'condition', e.target.value)}
                >
                    {CONDITION_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
            </div>

            {/* Cost & Price */}
            <div style={{ width: '90px' }}>
                <FormControl
                    type="number"
                    size="sm"
                    className="text-end x-small fw-bold"
                    value={unit.unitCost || ""}
                    onChange={(e) => handleUnitChange(itemIndex, uIdx, 'unitCost', e.target.value)}
                />
            </div>
            <div style={{ width: '90px' }}>
                <FormControl
                    type="number"
                    size="sm"
                    className="text-end x-small fw-bold text-primary"
                    value={unit.sellingPrice || ""}
                    onChange={(e) => handleUnitChange(itemIndex, uIdx, 'sellingPrice', e.target.value)}
                />
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-1">
                <Button variant="link" size="sm" className="p-0 text-warning" onClick={() => openTagModal(itemIndex, uIdx)}>
                    <BiWrench size={14} />
                </Button>
                <Button variant="link" size="sm" className="p-0 text-danger"
                    onClick={() => onQtyChange(item.purchaseQty - 1, itemIndex, item, uIdx)}>
                    <BiTrash size={14} />
                </Button>
            </div>
        </div>
    );
});

// --- MAIN GRID COMPONENT ---
const SerializedItemsGrid = ({
    item,
    itemIndex,
    duplicateSerials,
    setFieldValue,
    policies,
    onQtyChange,
    openTagModal,
    generateSerial
}) => {
    const CONDITION_LIST = ["Brand New", "Used Grade A", "Used Grade B", "Refurbished", "Damaged", "Used", "Other"];

    const handleUnitChange = useCallback((iIdx, uIdx, field, value) => {
        setFieldValue(`purchasedItems[${iIdx}].serializedItems[${uIdx}].${field}`, value);
    }, [setFieldValue]);

    if (!item.serializedItems || item.serializedItems.length === 0) return null;

    return (
        <div className="p-3 bg-light rounded-bottom border-top" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {item.serializedItems.map((unit, uIdx) => (
                <UnitRow
                    key={unit.tempId || uIdx}
                    unit={unit}
                    uIdx={uIdx}
                    itemIndex={itemIndex}
                    duplicateSerials={duplicateSerials}
                    handleUnitChange={handleUnitChange}
                    onQtyChange={onQtyChange}
                    item={item}
                    policies={policies}
                    openTagModal={openTagModal}
                    generateSerial={generateSerial}
                    CONDITION_LIST={CONDITION_LIST}
                />
            ))}
        </div>
    );
};

export default React.memo(SerializedItemsGrid);
